import React, { useState, useRef } from "react";
import api from "../../api/client";

const BASE = process.env.REACT_APP_EXPRESS_API_BASE_URL || "http://localhost:3001";

const placeholder = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='140' viewBox='0 0 200 140'%3E%3Crect width='200' height='140' fill='%2327272a'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' fill='%2352525b' font-size='13' font-family='sans-serif'%3ENo Image%3C/text%3E%3C/svg%3E";

function resolvePreview(url) {
  if (!url) return placeholder;
  if (url.startsWith("/uploads/")) return `${BASE}${url}`;
  return url;
}

// value = URL string (either external URL or /uploads/xxx.jpg)
// onChange(url) called when image changes
const ImageUploader = ({ value, onChange, label = "Gambar" }) => {
  const [tab, setTab] = useState("upload");
  const [urlInput, setUrlInput] = useState(value?.startsWith("/uploads/") ? "" : (value ?? ""));
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [error, setError] = useState(null);
  const fileRef = useRef();

  const compressImage = (file, maxPx = 1200, quality = 0.82) =>
    new Promise((resolve) => {
      // SVG and GIF are not compressible via canvas — skip
      if (file.type === "image/svg+xml" || file.type === "image/gif") {
        resolve(file);
        return;
      }
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        if (width <= maxPx && height <= maxPx) { resolve(file); return; }
        if (width > height) { height = Math.round((height / width) * maxPx); width = maxPx; }
        else { width = Math.round((width / height) * maxPx); height = maxPx; }
        const canvas = document.createElement("canvas");
        canvas.width = width; canvas.height = height;
        canvas.getContext("2d").drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => resolve(new File([blob], file.name, { type: "image/jpeg" })),
          "image/jpeg", quality
        );
      };
      img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
      img.src = url;
    });

  const handleFile = async (file) => {
    if (!file) return;
    const allowed = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowed.includes(file.type)) {
      setError("Format tidak didukung. Gunakan JPG, PNG, GIF, WEBP, atau SVG.");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError("Ukuran file max 10MB.");
      return;
    }
    setError(null);
    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const form = new FormData();
      form.append("file", compressed);
      const { data } = await api.post("/api/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onChange(data.url);
    } catch (err) {
      setError("Upload gagal. Pastikan backend berjalan.");
    } finally {
      setUploading(false);
    }
  };

  const handleUrlCommit = () => {
    onChange(urlInput.trim());
  };

  const preview = resolvePreview(value);

  return (
    <div>
      <label className="block mb-2 text-[10px] font-black uppercase tracking-widest text-zinc-500">
        {label}
      </label>

      {/* Tab Toggle */}
      <div className="flex gap-1 mb-3 bg-zinc-800 rounded-xl p-1 w-fit">
        {[
          { id: "upload", icon: "⬆", text: "Upload File" },
          { id: "url", icon: "🔗", text: "Paste URL" },
        ].map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${
              tab === t.id
                ? "bg-indigo-600 text-white shadow"
                : "text-zinc-400 hover:text-zinc-200"
            }`}
          >
            {t.icon} {t.text}
          </button>
        ))}
      </div>

      {tab === "upload" && (
        <div
          onClick={() => !uploading && fileRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFile(e.dataTransfer.files[0]);
          }}
          className={`relative flex flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed p-6 cursor-pointer transition-all ${
            dragOver
              ? "border-indigo-400 bg-indigo-950/30"
              : "border-zinc-700 hover:border-zinc-500 bg-zinc-900/40"
          } ${uploading ? "opacity-60 cursor-wait" : ""}`}
        >
          <input
            ref={fileRef}
            type="file"
            accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
            className="hidden"
            onChange={(e) => handleFile(e.target.files[0])}
          />
          {uploading ? (
            <div className="flex flex-col items-center gap-2">
              <div className="w-8 h-8 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-zinc-400">Mengupload...</span>
            </div>
          ) : (
            <>
              <svg className="w-8 h-8 text-zinc-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
              </svg>
              <p className="text-xs text-zinc-400 text-center">
                <span className="text-indigo-400 font-bold">Klik untuk pilih</span> atau drag & drop<br />
                <span className="text-zinc-600">JPG, PNG, GIF, WEBP, SVG · Max 10MB</span>
              </p>
            </>
          )}
        </div>
      )}

      {tab === "url" && (
        <div className="flex gap-2">
          <input
            type="text"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onBlur={handleUrlCommit}
            onKeyDown={(e) => e.key === "Enter" && handleUrlCommit()}
            placeholder="https://example.com/image.jpg"
            className="flex-1 bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-2 text-sm text-white placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          <button
            type="button"
            onClick={handleUrlCommit}
            className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-xl transition-colors"
          >
            Set
          </button>
        </div>
      )}

      {error && (
        <p className="mt-1.5 text-[10px] text-red-400 font-semibold">{error}</p>
      )}

      {/* Preview */}
      {value && (
        <div className="mt-3 relative group rounded-xl overflow-hidden border border-zinc-800 bg-zinc-900/50 h-36 flex items-center justify-center">
          <img
            src={preview}
            alt="preview"
            className="max-h-full max-w-full object-contain p-2 transition-transform duration-300 group-hover:scale-105"
            onError={(e) => { e.target.src = placeholder; }}
          />
          <button
            type="button"
            onClick={() => { onChange(""); setUrlInput(""); }}
            className="absolute top-2 right-2 p-1 bg-red-600/80 hover:bg-red-600 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete image"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};

export default ImageUploader;
